"use client";

import {
	Button,
	Heading,
	Icon,
	Image,
	Text,
	VideoPlayer
} from "@pamperedchef/design-system";
import type { Cta, CmsImage } from "@/composites/landing/types";
import * as styles from "./VideoCallout.module.scss";

export interface VideoCalloutProps {
	title?: string;
	body?: string;
	posterImage?: CmsImage;
	videoUrl?: string;
	cta?: Cta;
}

/**
 * Video feature callout — title, body, and an optional CTA next to a media
 * frame. When `videoUrl` is provided the frame plays via DS `VideoPlayer`
 * (poster + native controls); otherwise it falls back to the DS `Image`
 * poster with a play affordance that follows the CTA href. Mirrors the
 * Figma intent for the VIP-kit landing page (still play poster, click to
 * open the video).
 */
export function VideoCallout({
	title,
	body,
	posterImage,
	videoUrl,
	cta
}: VideoCalloutProps) {
	const playHref = cta?.href ?? "#";
	const playAriaLabel = cta?.title ?? "Play video";

	return (
		<section
			className={styles["video-callout"]}
			data-component="VideoCallout"
		>
			<div className={styles["video-callout__media"]}>
				{videoUrl ? (
					<VideoPlayer
						videoSrc={videoUrl}
						posterSrc={posterImage?.url}
						controls
						containerClassName={styles["video-callout__player"]}
						videoClassName={styles["video-callout__video"]}
					/>
				) : (
					<>
						{posterImage ? (
							<Image
								src={posterImage.url}
								alt={posterImage.alt ?? title ?? "Video poster"}
								className={styles["video-callout__poster"]}
								objectFit="cover"
							/>
						) : (
							<div
								className={
									styles["video-callout__poster-fallback"]
								}
								aria-hidden="true"
							/>
						)}
						<a
							href={playHref}
							className={styles["video-callout__play"]}
							aria-label={playAriaLabel}
						>
							<Icon name="play" size={28} />
						</a>
					</>
				)}
			</div>

			<div className={styles["video-callout__content"]}>
				{title ? (
					<Heading
						tag="h2"
						styledAs="h3"
						className={styles["video-callout__title"]}
					>
						{title}
					</Heading>
				) : null}
				{body ? (
					<Text
						tag="p"
						styledAs="mdr"
						className={styles["video-callout__body"]}
					>
						{body}
					</Text>
				) : null}
				{cta ? (
					<Button
						kind="secondary"
						size="lg"
						onPress={() => {
							window.location.href = cta.href;
						}}
					>
						{cta.title}
					</Button>
				) : null}
			</div>
		</section>
	);
}
